import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatCurrencyThousands,
  SourceFaithfulChartCard,
  sourceFaithfulChartAxisTick,
  sourceFaithfulChartLegendStyle,
  sourceFaithfulPalette,
  sourceFaithfulChartTooltipStyle,
} from './SourceFaithfulRecharts';

export type TuitionCollectionTrendDatum = {
  month: string;
  monthKey?: string;
  tuition?: number;
  fees?: number;
  due?: number;
  outstanding?: number;
  collected?: number;
  billed?: number;
  billedLine?: number;
  seriesMax?: number;
  dueCents?: number;
  outstandingCents?: number;
  collectedCents?: number;
  billedCents?: number;
};

type TuitionCollectionTrendChartProps = {
  data: readonly TuitionCollectionTrendDatum[];
  ariaLabel: string;
  testId?: string;
  title?: string;
  subtitle?: string;
  emptyStateTitle?: string;
  emptyStateBody?: string;
};

export function TuitionCollectionTrendChart({
  data,
  ariaLabel,
  testId,
  title = 'Collection trend',
  subtitle,
  emptyStateTitle = 'No chart data yet',
  emptyStateBody = 'This chart will populate after tuition activity arrives.',
}: TuitionCollectionTrendChartProps) {
  const readFiniteNumber = (value: unknown): number => (
    typeof value === 'number' && Number.isFinite(value) ? value : 0
  );
  const chartData = data.map((point) => {
    const hasAuthoritativeBilled = Object.prototype.hasOwnProperty.call(point, 'billed');
    const billed = hasAuthoritativeBilled
      ? readFiniteNumber(point.billed)
      : readFiniteNumber(point.tuition) + readFiniteNumber(point.fees);
    return {
      ...point,
      billed,
      collected: readFiniteNumber(point.collected),
      due: readFiniteNumber(point.due),
      outstanding: readFiniteNumber(point.outstanding),
      seriesMax: readFiniteNumber(point.seriesMax),
    };
  });
  const hasVisibleSeries = chartData.some((point) => (
    point.billed !== 0
    || point.collected !== 0
    || point.due !== 0
    || point.outstanding !== 0
  ));
  const yAxisMin = chartData.reduce(
    (minimum, point) => Math.min(
      minimum,
      point.billed,
      point.collected,
      point.due,
      point.outstanding,
    ),
    0,
  );
  const yAxisMax = chartData.reduce(
    (maximum, point) => Math.max(
      maximum,
      point.billed,
      point.collected,
      point.due,
      point.outstanding,
      point.seriesMax,
    ),
    0,
  );

  return (
    <SourceFaithfulChartCard
      title={title}
      subtitle={subtitle}
      chartAriaLabel={ariaLabel}
      chartHeightPx={240}
      testId={testId}
    >
      {hasVisibleSeries ? (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            accessibilityLayer
            data={chartData}
            margin={{ top: 8, right: 12, left: -18, bottom: 0 }}
            barSize={26}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={sourceFaithfulPalette.border} />
            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={sourceFaithfulChartAxisTick} dy={10} />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={sourceFaithfulChartAxisTick}
              tickFormatter={formatCurrencyThousands}
              width={46}
              domain={[yAxisMin, yAxisMax]}
            />
            <Tooltip
              cursor={{ fill: sourceFaithfulPalette.muted }}
              contentStyle={sourceFaithfulChartTooltipStyle}
              formatter={(value: number | string, name: string | number) => [formatCurrencyThousands(value), String(name)]}
            />
            <Legend wrapperStyle={sourceFaithfulChartLegendStyle} iconType="circle" verticalAlign="top" align="right" />
            <Bar dataKey="collected" name="Collected" fill={sourceFaithfulPalette.jungle} radius={[4, 4, 0, 0]} />
            <Line dataKey="billed" name="Billed" stroke={sourceFaithfulPalette.primary} strokeWidth={2} dot={false} />
            <Line dataKey="due" name="Due" stroke={sourceFaithfulPalette.aquamarine} strokeWidth={2} dot={false} />
            <Line dataKey="outstanding" name="Outstanding" stroke={sourceFaithfulPalette.strawberry} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex h-full items-center justify-center">
          <div className="max-w-[30rem] rounded-xl border border-dashed border-border bg-white px-5 py-4 text-left shadow-sm">
            <p className="text-sm font-semibold text-iron-grey">{emptyStateTitle}</p>
            <p className="mt-2 text-sm text-text-secondary">{emptyStateBody}</p>
          </div>
        </div>
      )}
    </SourceFaithfulChartCard>
  );
}
